import React, { useEffect, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { fetchCurrentSiteSettings, getAuthToken, setAuthToken } from '../services/api';
import {
    getCurrentSiteKey,
    setCurrentSiteKey,
    SITE_CONFIGS,
    SITE_OPTIONS,
    SiteKey,
    subscribeToSiteBrandingUpdate,
    subscribeToSiteChange,
} from '../services/site';

type NavigationProps = {
    currentGalleryTitle?: string | null;
};

const Navigation: React.FC<NavigationProps> = ({ currentGalleryTitle }) => {
    const isLoggedIn = Boolean(getAuthToken());
    const history = useHistory();
    const location = useLocation();
    const isAdminRoute = location.pathname.startsWith('/admin');
    const isGalleryRoute = location.pathname.startsWith('/gallery/');
    const isHomeRoute = location.pathname === '/';
    const [isScrolled, setIsScrolled] = useState(false);
    const [siteKey, setSiteKeyState] = useState<SiteKey>(getCurrentSiteKey());
    const [runtimeBranding, setRuntimeBranding] = useState<{ name?: string | null; tagline?: string | null; logoUrl?: string | null } | null>(null);
    const currentSite = SITE_CONFIGS[siteKey];

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 24);
        };

        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    useEffect(() => subscribeToSiteChange(setSiteKeyState), []);

    useEffect(() => {
        let isActive = true;

        const loadBranding = async () => {
            try {
                const settings = await fetchCurrentSiteSettings(siteKey);
                if (isActive) {
                    setRuntimeBranding(settings || null);
                }
            } catch (error) {
                if (isActive) {
                    setRuntimeBranding(null);
                }
            }
        };

        loadBranding();

        return () => {
            isActive = false;
        };
    }, [siteKey]);

    const handleSiteChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setCurrentSiteKey(event.target.value);
    };

    const handleLogout = () => {
        setAuthToken(null);
        history.replace('/login');
    };

    const displayName = (runtimeBranding?.name || '').trim() || currentSite.name;
    const displayTagline = (runtimeBranding?.tagline || '').trim() || currentSite.tagline;
    const displayLogoUrl = (runtimeBranding?.logoUrl || '').trim();
    const displayLogoSrc = displayLogoUrl ? encodeURI(displayLogoUrl) : '';

    useEffect(() => {
        return subscribeToSiteBrandingUpdate((updatedSiteKey) => {
            if (updatedSiteKey !== siteKey) return;
            fetchCurrentSiteSettings(siteKey)
                .then((settings) => setRuntimeBranding(settings || null))
                .catch(() => setRuntimeBranding(null));
        });
    }, [siteKey]);

    if (isAdminRoute) {
        return (
            <nav className="top-nav top-nav--admin">
                <div className="top-nav__brand">
                    <Link
                        className="top-nav__logo"
                        to="/"
                        aria-label={`${displayName} - ${displayTagline}`}
                    >
                        {displayLogoUrl ? (
                            <span className="top-nav__logo-image-wrap">
                                <img className="top-nav__logo-image" src={displayLogoSrc} alt={displayName} />
                            </span>
                        ) : (
                            <>
                                <span className="top-nav__logo-main">{displayName}</span>
                                <span className="top-nav__logo-sub">- {displayTagline}</span>
                            </>
                        )}
                    </Link>
                    {isGalleryRoute && currentGalleryTitle && (
                        <span className="top-nav__context" aria-label="Aktivt galleri">
                            {currentGalleryTitle}
                        </span>
                    )}
                </div>
                <ul className="top-nav__links">
                    <li>
                        <label className="site-selector" aria-label="Vælg site">
                            <span>Site</span>
                            <select value={siteKey} onChange={handleSiteChange}>
                                {SITE_OPTIONS.map((site) => (
                                    <option key={site.key} value={site.key}>{site.name}</option>
                                ))}
                            </select>
                        </label>
                    </li>
                    <li>
                        <Link to="/galleries">Gallerier</Link>
                    </li>
                    <li>
                        <Link to="/admin">Admin</Link>
                    </li>
                    <li>
                        <Link to="/admin/site-settings">Site indstillinger</Link>
                    </li>
                    <li>
                        <Link to="/admin#home-blocks">Forside blokke</Link>
                    </li>
                    <li>
                        <Link to="/admin#galleries">Galleri liste</Link>
                    </li>
                    <li>
                        <Link to="/admin#images">Billedliste</Link>
                    </li>
                    <li>
                        <Link to="/admin#orders">Ordrer</Link>
                    </li>
                    <li>
                        <button type="button" onClick={handleLogout}>Logout</button>
                    </li>
                </ul>
            </nav>
        );
    }

    return (
        <nav className={`top-nav top-nav--public${isHomeRoute ? ' top-nav--home' : ''}${isScrolled ? ' top-nav--scrolled' : ''}`}>
            <div className="top-nav__side top-nav__side--left">
                <Link to="/galleries">Gallery</Link>
            </div>
            <div className="top-nav__brand top-nav__brand--centered">
                <Link
                    className="top-nav__logo"
                    to="/"
                    aria-label={`${displayName} - ${displayTagline}`}
                >
                    {displayLogoUrl ? (
                        <span className="top-nav__logo-image-wrap">
                            <img className="top-nav__logo-image" src={displayLogoSrc} alt={displayName} />
                        </span>
                    ) : (
                        <>
                            <span className="top-nav__logo-main">{displayName}</span>
                            <span className="top-nav__logo-sub">- {displayTagline}</span>
                        </>
                    )}
                </Link>
                {isGalleryRoute && currentGalleryTitle && (
                    <span className="top-nav__context top-nav__context--centered" aria-label="Aktivt galleri">
                        {currentGalleryTitle}
                    </span>
                )}
            </div>
            <div className="top-nav__side top-nav__side--right">
                <Link to="/#home-contact">Contact</Link>
                {isLoggedIn ? (
                    <button type="button" onClick={handleLogout}>Logout</button>
                ) : (
                    <Link to="/login">Login</Link>
                )}
            </div>
        </nav>
    );
};

export default Navigation;